import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import {
  downloadFulfillmentOrderArchive,
  uploadFulfillmentOrderArchive,
} from "../repo";
import type { FulfillmentOrderArchive } from "../types";
import {
  FulfillmentOrderUserFacingError,
  getSafeFulfillmentOrderActionErrorMessage,
  FulfillmentOrderStaleActionError,
  validateArchiveFile,
} from "../service/supplierConfirmationService";
import { downloadFulfillmentOrderAgreementPdf } from "../service/fulfillmentOrderPdfService";

type AgreementFileAction = "download" | "downloadArchive" | "upload";
type AgreementTarget = {
  fulfillmentOrderId: string | null;
  documentRevision: number | null;
  generation: number;
};
type AgreementMutex = AgreementTarget & {
  kind: AgreementFileAction;
  actionKey: string;
  promise: Promise<unknown>;
};
type CommittedUpload = AgreementTarget & {
  actionKey: string;
  result: unknown;
};
type AgreementActionState = AgreementTarget & {
  kind: AgreementFileAction | null;
  error: Error | null;
};

/** 当前确认协议只提供 PDF 下载和盖章协议上传，不保留发送、撤回、定稿或多版本动作。 */
export function useFulfillmentOrderLifecycleRuntime(
  fulfillmentOrderId: string | null,
  documentRevision: number | null,
) {
  const queryClient = useQueryClient();
  const targetRef = useRef<AgreementTarget>({
    fulfillmentOrderId,
    documentRevision,
    generation: 0,
  });
  const mutexRef = useRef<AgreementMutex | null>(null);
  const committedUploadRef = useRef<Map<string, CommittedUpload>>(new Map());
  if (
    targetRef.current.fulfillmentOrderId !== fulfillmentOrderId
    || targetRef.current.documentRevision !== documentRevision
  ) {
    targetRef.current = {
      fulfillmentOrderId,
      documentRevision,
      generation: targetRef.current.generation + 1,
    };
    mutexRef.current = null;
  }
  const generation = targetRef.current.generation;
  const [state, setState] = useState<AgreementActionState>({
    fulfillmentOrderId,
    documentRevision,
    generation,
    kind: null,
    error: null,
  });

  const isCurrent = useCallback((target: AgreementTarget) => (
    targetRef.current.fulfillmentOrderId === target.fulfillmentOrderId
    && targetRef.current.documentRevision === target.documentRevision
    && targetRef.current.generation === target.generation
  ), []);

  const refresh = useCallback(async (orderId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["fulfillmentOrder", "detail", orderId],
        exact: true,
      }),
      queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "workspace"] }),
      queryClient.invalidateQueries({ queryKey: ["fulfillmentOrder", "list"] }),
    ]);
  }, [queryClient]);

  const runAction = useCallback(<T,>(
    kind: AgreementFileAction,
    actionKey: string,
    operation: () => Promise<T>,
    onSuccess?: (result: T) => void | Promise<void>,
    remoteUpload = false,
  ): Promise<T> => {
    const target: AgreementTarget = { fulfillmentOrderId, documentRevision, generation };
    const targetKey = JSON.stringify([fulfillmentOrderId, documentRevision]);
    const committed = committedUploadRef.current.get(targetKey);
    if (committed && (kind !== "upload" || committed.actionKey !== actionKey)) {
      return Promise.reject(new FulfillmentOrderUserFacingError(
        "上次盖章协议已上传成功但页面刷新失败，请重试原文件完成刷新。",
      ));
    }
    const existing = mutexRef.current;
    if (
      existing
      && existing.fulfillmentOrderId === fulfillmentOrderId
      && existing.documentRevision === documentRevision
      && existing.generation === generation
    ) {
      if (existing.kind === kind && existing.actionKey === actionKey) {
        return existing.promise as Promise<T>;
      }
      return Promise.reject(new FulfillmentOrderUserFacingError(
        "已有协议文件操作正在处理，请稍候。",
      ));
    }

    setState({ ...target, kind, error: null });
    const operationPromise = committed
      ? Promise.resolve(committed.result as T)
      : operation();
    const promise = operationPromise
      .then(async (result) => {
        if (remoteUpload && !committed) {
          committedUploadRef.current.set(targetKey, { ...target, actionKey, result });
        }
        // 已成功的上传必须刷新原协议；generation 只隔离当前界面的状态与提示。
        if (!remoteUpload && !isCurrent(target)) throw new FulfillmentOrderStaleActionError();
        await onSuccess?.(result);
        if (remoteUpload) committedUploadRef.current.delete(targetKey);
        if (!isCurrent(target)) throw new FulfillmentOrderStaleActionError();
        setState({ ...target, kind: null, error: null });
        return result;
      })
      .catch((error: unknown) => {
        if (!isCurrent(target)) throw new FulfillmentOrderStaleActionError();
        if (error instanceof FulfillmentOrderStaleActionError) throw error;
        const safeError = new FulfillmentOrderUserFacingError(
          getSafeFulfillmentOrderActionErrorMessage(error, "协议文件操作失败，请稍后重试"),
        );
        setState({ ...target, kind: null, error: safeError });
        throw safeError;
      })
      .finally(() => {
        if (mutexRef.current?.promise === promise) mutexRef.current = null;
      });
    mutexRef.current = { ...target, kind, actionKey, promise };
    return promise;
  }, [documentRevision, fulfillmentOrderId, generation, isCurrent]);

  const download = useCallback((source: HTMLElement, fileName: string) => {
    if (!fulfillmentOrderId || !documentRevision) {
      return Promise.reject(new FulfillmentOrderUserFacingError("协议尚未就绪"));
    }
    const target = { fulfillmentOrderId, documentRevision, generation };
    return runAction(
      "download",
      JSON.stringify([fileName]),
      () => downloadFulfillmentOrderAgreementPdf(source, fileName, () => {
        if (!isCurrent(target)) throw new FulfillmentOrderStaleActionError();
      }),
    );
  }, [documentRevision, fulfillmentOrderId, generation, isCurrent, runAction]);

  const upload = useCallback((file: File) => {
    if (!fulfillmentOrderId || !documentRevision) {
      return Promise.reject(new FulfillmentOrderUserFacingError("协议尚未就绪"));
    }
    const validationError = validateArchiveFile(file);
    if (validationError) {
      return Promise.reject(new FulfillmentOrderUserFacingError(validationError));
    }
    const actionKey = JSON.stringify([file.name, file.size, file.type, file.lastModified]);
    return runAction(
      "upload",
      actionKey,
      () => uploadFulfillmentOrderArchive(fulfillmentOrderId, documentRevision, file),
      () => refresh(fulfillmentOrderId),
      true,
    );
  }, [documentRevision, fulfillmentOrderId, refresh, runAction]);

  const downloadArchive = useCallback((archive: FulfillmentOrderArchive) => {
    if (!fulfillmentOrderId || archive.fulfillmentOrderId !== fulfillmentOrderId) {
      return Promise.reject(new FulfillmentOrderUserFacingError("盖章附件与当前协议不匹配"));
    }
    return runAction(
      "downloadArchive",
      JSON.stringify([archive.id, archive.fileSha256]),
      async () => {
        const download = await downloadFulfillmentOrderArchive(archive);
        const url = window.URL.createObjectURL(download.blob);
        const link = document.createElement("a");
        try {
          link.href = url;
          link.download = download.fileName;
          document.body.appendChild(link);
          link.click();
        } finally {
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      },
    );
  }, [fulfillmentOrderId, runAction]);

  const currentState = state.fulfillmentOrderId === fulfillmentOrderId
    && state.documentRevision === documentRevision
    && state.generation === generation
    ? state
    : {
      fulfillmentOrderId,
      documentRevision,
      generation,
      kind: null,
      error: null,
    };

  return {
    download,
    downloadArchive,
    upload,
    actionPending: currentState.kind !== null,
    exporting: currentState.kind === "download",
    uploading: currentState.kind === "upload",
    error: currentState.error,
  };
}
